import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  inject,
  input,
  output,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastrService } from 'ngx-toastr';
import type { AvatarCropResult } from './avatar-crop.model';

/** Resolución final del avatar (cuadrado JPEG). */
const OUTPUT_SIZE = 256;
const JPEG_QUALITY = 0.82;
const MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const VIEWPORT = 280;

@Component({
  selector: 'app-avatar-uploader',
  templateUrl: './avatar-uploader.component.html',
  styleUrl: './avatar-uploader.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule]
})
export class AvatarUploaderComponent {
  private readonly toastr = inject(ToastrService);

  /** URL actual del avatar (servidor o preview). */
  readonly avatarUrl = input<string | null>(null);
  /** Deshabilita la interacción (p. ej. mientras sube). */
  readonly disabled = input(false);
  /** Etiqueta del botón principal. */
  readonly buttonLabel = input('Subir foto');

  readonly avatarChange = output<AvatarCropResult>();

  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('viewport') private viewportRef?: ElementRef<HTMLDivElement>;

  readonly cropDialogVisible = signal(false);
  readonly sourceUrl = signal<string | null>(null);
  readonly imageNaturalWidth = signal(0);
  readonly imageNaturalHeight = signal(0);
  readonly zoom = signal(1);
  readonly offsetX = signal(0);
  readonly offsetY = signal(0);
  readonly dragging = signal(false);
  readonly processing = signal(false);

  private dragStartX = 0;
  private dragStartY = 0;
  private offsetStartX = 0;
  private offsetStartY = 0;
  private objectUrl: string | null = null;

  readonly previewDisplay = computed(() => this.avatarUrl());

  readonly imageStyle = computed(() => {
    const nw = this.imageNaturalWidth();
    const nh = this.imageNaturalHeight();
    if (!nw || !nh) return {};

    const baseScale = Math.max(VIEWPORT / nw, VIEWPORT / nh);
    const scale = baseScale * this.zoom();
    const displayW = nw * scale;
    const displayH = nh * scale;

    return {
      width: `${displayW}px`,
      height: `${displayH}px`,
      transform: `translate(${this.offsetX()}px, ${this.offsetY()}px)`,
      left: `${(VIEWPORT - displayW) / 2}px`,
      top: `${(VIEWPORT - displayH) / 2}px`
    };
  });

  openFilePicker(): void {
    if (this.disabled()) return;
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      this.toastr.error('Solo se permiten imágenes JPG o PNG');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      this.toastr.error('La imagen no debe superar 8 MB');
      return;
    }

    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(file);
    this.sourceUrl.set(this.objectUrl);
    this.zoom.set(1);
    this.offsetX.set(0);
    this.offsetY.set(0);
    this.cropDialogVisible.set(true);
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.imageNaturalWidth.set(img.naturalWidth);
    this.imageNaturalHeight.set(img.naturalHeight);
    this.zoom.set(1);
    this.offsetX.set(0);
    this.offsetY.set(0);
  }

  onZoomChange(value: number | string): void {
    const next = typeof value === 'string' ? Number(value) : value;
    if (Number.isNaN(next)) return;
    this.zoom.set(next);
    this.clampOffset();
  }

  onDialogVisibleChange(visible: boolean): void {
    this.cropDialogVisible.set(visible);
    if (!visible) {
      this.resetCropState();
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.sourceUrl()) return;
    this.dragging.set(true);
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.offsetStartX = this.offsetX();
    this.offsetStartY = this.offsetY();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging()) return;
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;
    this.offsetX.set(this.offsetStartX + dx);
    this.offsetY.set(this.offsetStartY + dy);
    this.clampOffset();
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.dragging()) return;
    this.dragging.set(false);
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }

  cancelCrop(): void {
    this.cropDialogVisible.set(false);
  }

  async confirmCrop(): Promise<void> {
    const src = this.sourceUrl();
    if (!src || this.processing()) return;

    this.processing.set(true);
    try {
      const result = await this.renderCroppedAvatar(src);
      this.avatarChange.emit(result);
      this.cropDialogVisible.set(false);
    } finally {
      this.processing.set(false);
    }
  }

  private async renderCroppedAvatar(src: string): Promise<AvatarCropResult> {
    const img = await this.loadImage(src);
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const baseScale = Math.max(VIEWPORT / nw, VIEWPORT / nh);
    const scale = baseScale * this.zoom();
    const displayW = nw * scale;
    const displayH = nh * scale;

    const imgLeft = (VIEWPORT - displayW) / 2 + this.offsetX();
    const imgTop = (VIEWPORT - displayH) / 2 + this.offsetY();

    // Región del viewport (cuadrado completo) mapeada a coordenadas de la imagen fuente
    const sx = (0 - imgLeft) / scale;
    const sy = (0 - imgTop) / scale;
    const sSize = VIEWPORT / scale;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No se pudo crear el canvas');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const base64 = dataUrl.split(',')[1] ?? '';

    return {
      base64,
      contentType: 'image/jpeg',
      previewUrl: dataUrl
    };
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
      img.src = src;
    });
  }

  private clampOffset(): void {
    const nw = this.imageNaturalWidth();
    const nh = this.imageNaturalHeight();
    if (!nw || !nh) return;

    const baseScale = Math.max(VIEWPORT / nw, VIEWPORT / nh);
    const scale = baseScale * this.zoom();
    const displayW = nw * scale;
    const displayH = nh * scale;

    const maxX = Math.max(0, (displayW - VIEWPORT) / 2);
    const maxY = Math.max(0, (displayH - VIEWPORT) / 2);

    this.offsetX.set(Math.min(maxX, Math.max(-maxX, this.offsetX())));
    this.offsetY.set(Math.min(maxY, Math.max(-maxY, this.offsetY())));
  }

  private resetCropState(): void {
    this.revokeObjectUrl();
    this.sourceUrl.set(null);
    this.imageNaturalWidth.set(0);
    this.imageNaturalHeight.set(0);
    this.zoom.set(1);
    this.offsetX.set(0);
    this.offsetY.set(0);
    this.dragging.set(false);
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
